import { all, takeLatest, put, call, select } from 'redux-saga/effects';

import cookie from '@snapask/common-components/dist/utils/cookie';

import config from '@dragonfruit/config';
const { COOKIE_DOMAIN } = config;

import {
  apiFetchRegionAuthType,
  apiVerifyPhoneSignup,
  apiPhoneSignup,
  apiEmailSignup,
} from '@dragonfruit/services/apis';

import {
  ONBOARDING_SIGNUP_CHECK_EMAIL_PAGE_ENTER,
  COMPLETE_REGISTRATION,
  EVENT_COMPLETED_REGISTRATION,
} from '@dragonfruit/constants/gtmEvents';
import { ALERT_ERROR_THEME } from '@dragonfruit/constants/alertBannerThemes';
import { STUDENT, TUTOR } from '@dragonfruit/constants/userRoles';
import { PHONE, EMAIL } from '@dragonfruit/constants/signupAuthType';

import {
  getSignupCountryCode,
  getSignupPhoneNumber,
} from '@dragonfruit/redux/signup';
import {
  SIGNUP,
  signupSuccess,
  signupFailed,
  VERIFY_PHONE_SIGNUP,
  verifyPhoneSignupSuccess,
  verifyPhoneSignupFailure,
  storeSignupInfo,
  FETCH_REGION_PROVIDER,
  fetchRegionProviderSuccess,
  getSignupInfo,
  getSignupRole,
  getStudentSignupAuthType,
  getTutorSignupAuthType,
} from '@dragonfruit/redux/signup';
import { sendGtmEvent } from '@dragonfruit/redux/googleTagManager';
import { showAlertBanner } from '@dragonfruit/redux/alertBanner';
import { getPartnerCode } from '@dragonfruit/redux/location';
import { verifyTutorEmail } from '@dragonfruit/redux/navigation';
import { getRegion } from '@dragonfruit/redux/locale';
import { storeProfile } from '@dragonfruit/redux/profile';
import { loginSuccess } from '@dragonfruit/redux/auth';

import { getQuestionId } from '@dragonfruit/utils/temporaryQuestion';

export function* signupFlow(action) {
  const {
    region: { name: regionName } = {},
    email,
    countryCode,
    phoneNumber,
    password,
  } = action.payload;

  try {
    const snapCookie = new cookie(COOKIE_DOMAIN);
    const appsflyerConversion = snapCookie.getAppsFlyerConversionData();
    const role = yield select(getSignupRole);
    const studentSignupAuthType = yield select(getStudentSignupAuthType);
    const tutorSignupAuthType = yield select(getTutorSignupAuthType);
    const partnerCode = yield select(getPartnerCode);
    const { region: signupInfoRegion } = yield select(getSignupInfo);

    const questionId = getQuestionId();
    const region = regionName || signupInfoRegion;
    const isStudent = role === STUDENT;

    const authTypeMap = {
      [TUTOR]: tutorSignupAuthType,
      [STUDENT]: studentSignupAuthType,
    };
    const signupAPIMap = {
      [PHONE]: apiPhoneSignup,
      [EMAIL]: apiEmailSignup,
    };

    const authType = authTypeMap[role];
    const isPhoneType = authType === PHONE;

    const data = {
      region_name: region,
      email,
      country_code: countryCode,
      phone: phoneNumber,
      password,
      application_uid: partnerCode,
      role,
    };

    if (isStudent && questionId) {
      data.question_id = questionId;
    }

    if (appsflyerConversion) {
      data.appsflyer_conversion = JSON.stringify(appsflyerConversion);
    }

    yield put(storeSignupInfo({ region, email, countryCode, phoneNumber }));
    yield call(signupAPIMap[authType], data);
    yield put(signupSuccess({ isStudent, isPhoneType }));

    if (isPhoneType) {
      return;
    }

    if (isStudent) {
      yield put(
        sendGtmEvent({
          eventName: ONBOARDING_SIGNUP_CHECK_EMAIL_PAGE_ENTER,
        })
      );
      return;
    }

    yield put(verifyTutorEmail());
  } catch (err) {
    yield put(signupFailed(err.message));
    yield put(
      showAlertBanner({
        message: err.message,
        theme: ALERT_ERROR_THEME,
      })
    );
  }
}

export function* fetchRegionProviderFlow(action) {
  try {
    const appRegion = yield select(getRegion);
    const { region = appRegion } = action.payload;

    const { providers, studentRegisterType } = yield call(
      apiFetchRegionAuthType,
      region
    );

    yield put(storeSignupInfo({ region }));
    yield put(
      fetchRegionProviderSuccess({
        providers,
        studentSignupAuthType: studentRegisterType,
      })
    );
  } catch (err) {
    yield put(
      showAlertBanner({
        message: err.message,
        theme: ALERT_ERROR_THEME,
      })
    );
  }
}

export function* verifyPhoneSignupFlow(action) {
  try {
    const { verificationCode } = action.payload;
    const countryCode = yield select(getSignupCountryCode);
    const phoneNumber = yield select(getSignupPhoneNumber);

    const {
      applicationName, // the purpose of application-name is to indicate where users come from
      firstName,
      lastName,
      id,
      username,
      email,
      profilePicUrl: avatar,
      role: { name: role },
      region,
      phone,
      jwt,
    } = yield call(apiVerifyPhoneSignup, {
      countryCode,
      phoneNumber,
      verificationCode,
    });

    const profile = {
      applicationName,
      userId: id.toString(),
      username,
      email,
      role,
      firstName,
      lastName,
      countryCode,
      avatar,
      region: region.name,
      phone,
    };

    const snapCookie = new cookie(COOKIE_DOMAIN);
    snapCookie.saveCookieFromAuthInfo(profile);
    snapCookie.updateUserToken(jwt);

    // store profile info
    yield put(storeProfile(profile));
    yield put(loginSuccess(jwt));

    yield put(
      sendGtmEvent({
        eventName: COMPLETE_REGISTRATION,
        data: {
          content_name: profile.role,
          currency: region.fullName,
          value: id,
          provider: PHONE,
        },
      })
    );

    yield put(
      sendGtmEvent({
        eventName: EVENT_COMPLETED_REGISTRATION,
        data: {
          sourceType: PHONE,
        },
      })
    );

    yield put(verifyPhoneSignupSuccess());
  } catch (err) {
    yield put(verifyPhoneSignupFailure(err.message));
    yield put(
      showAlertBanner({
        message: err.message,
        theme: ALERT_ERROR_THEME,
      })
    );
  }
}

export default function*() {
  yield all([
    takeLatest(SIGNUP, signupFlow),
    takeLatest(FETCH_REGION_PROVIDER, fetchRegionProviderFlow),
    takeLatest(VERIFY_PHONE_SIGNUP, verifyPhoneSignupFlow),
  ]);
}



// WEBPACK FOOTER //
// ./app/sagas/signup/snapask.js